import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { authService } from '@/services/authService';
import { toAppError } from '@/lib/errors';
import type { LoginResponse, MfaChallengeResponse, MfaSetupResponse } from '@/types';

interface MfaChallengeFormProps {
  challenge: MfaChallengeResponse;
  onCompleted: (session: LoginResponse) => void;
  onCancel: () => void;
}

export function MfaChallengeForm({ challenge, onCompleted, onCancel }: MfaChallengeFormProps): JSX.Element {
  const enroll = challenge.status === 'mfa_enrollment';
  const [setup, setSetup] = useState<MfaSetupResponse | null>(null);
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(enroll);

  useEffect(() => {
    if (!enroll) return;
    let cancelled = false;
    setLoading(true);
    void authService
      .mfaSetup(challenge.mfaToken)
      .then((result) => {
        if (!cancelled) setSetup(result);
      })
      .catch((err) => {
        if (!cancelled) setError(toAppError(err).message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [challenge.mfaToken, enroll]);

  const onSubmit = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const session = enroll
        ? await authService.mfaEnable(challenge.mfaToken, code)
        : await authService.mfaVerify(challenge.mfaToken, code);
      onCompleted(session);
    } catch (err) {
      setError(toAppError(err).message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box component="form" onSubmit={(event) => void onSubmit(event)} noValidate>
      <Stack spacing={4}>
        <Typography variant="body2" color="text.secondary">
          {enroll
            ? 'Per accedere devi attivare l’autenticazione a due fattori. Scansiona il QR con Google Authenticator, Microsoft Authenticator o un’app TOTP equivalente, poi inserisci il codice a 6 cifre.'
            : 'Inserisci il codice a 6 cifre della tua app autenticatore, oppure un codice di backup.'}
        </Typography>

        {error && (
          <Alert severity="error" onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {enroll && setup && (
          <Stack spacing={3} alignItems="center">
            <Box
              component="img"
              src={setup.qrDataUrl}
              alt="QR code per configurare l’autenticatore"
              sx={{ width: 200, height: 200, bgcolor: 'common.white', p: 1, borderRadius: 1 }}
            />
            <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-all', textAlign: 'center' }}>
              Chiave manuale: {setup.secret}
            </Typography>
          </Stack>
        )}

        <TextField
          label={enroll ? 'Codice di conferma' : 'Codice MFA'}
          value={code}
          onChange={(event) => setCode(event.target.value)}
          autoComplete="one-time-code"
          inputMode="numeric"
          required
          autoFocus={!enroll}
          disabled={enroll && !setup}
        />

        <Button
          type="submit"
          variant="contained"
          size="large"
          disabled={loading || !code.trim() || (enroll && !setup)}
          startIcon={loading ? <CircularProgress size={18} color="inherit" /> : undefined}
        >
          {loading ? 'Verifica…' : enroll ? 'Attiva e accedi' : 'Verifica e accedi'}
        </Button>

        <Button type="button" variant="text" onClick={onCancel} disabled={loading}>
          Torna all’accesso
        </Button>
      </Stack>
    </Box>
  );
}
